import { Table, TableBody, Button } from '@/components/ui';
import { DataTablePagination, PanelInfo } from '@/components/custom';
import { broadcastService } from '@/services';
import { useUserConnected } from '@/hooks';
import { useRequestDeleteEvent } from '@/hooks/httpRequest/events';
import { EventStatusEnum } from '@/enums';
import { getApiErrorMessage, notify } from '@/functions';
import { Calendar, AlertTriangle } from 'lucide-react';
import type { IEvent } from '@/interfaces';
import DataTableHeader from './DataTableHeader';
import DataTableBodySkeleton from './DataTableBodySkeleton';
import DataTableMobileSkeleton from './DataTableMobileSkeleton';
import { DataTableDesktopRow, DataTableMobileCard } from './components';
import type useEvents from '../../hooks/useEvents';

type TProps = {
	data: ReturnType<typeof useEvents>;
	onViewDetails: (event: IEvent) => void;
};

const DataTable = ({ data, onViewDetails }: TProps) => {
	const { events, isLoading } = data;
	const { userConnected } = useUserConnected();
	const isRoot = !!userConnected?.root;

	const { triggerDeleteEvent } = useRequestDeleteEvent({
		onSuccess: () => { notify('Événement supprimé.'); },
	});

	/* An event that has started runs to its end: only a scheduled one can be deleted */
	const canDelete = (event: IEvent) => isRoot && event.status === EventStatusEnum.SCHEDULED;

	const handleDeleteClick = (event: IEvent) => {
		broadcastService.modal.show({
			content: (
				<div className="flex flex-col items-center text-center space-y-4 py-2">
					<div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
						<AlertTriangle className="h-6 w-6 text-destructive" />
					</div>
					<div className="space-y-1">
						<p className="font-medium text-foreground">Supprimer l'événement ?</p>
						<p className="text-xs text-muted-foreground">Cette action est irréversible.</p>
						{!!event.price && event.metrics.registeredCount > 0 && (
							<p className="text-xs text-muted-foreground">
								{event.metrics.registeredCount > 1
									? `Les ${event.metrics.registeredCount} inscrits seront remboursés de ${event.price} pièces chacun.`
									: `L'inscrit sera remboursé de ${event.price} pièces.`}
							</p>
						)}
					</div>
					<div className="flex space-x-2 pt-2 w-full">
						<Button variant="outline" onClick={() => broadcastService.modal.hide()} size="sm" className="flex-1">
							Annuler
						</Button>
						<Button
							variant="destructive"
							onClick={async () => {
								broadcastService.modal.hide();
								try { await triggerDeleteEvent(event._id); }
								catch (error) { notify(getApiErrorMessage(error, 'Erreur lors de la suppression.'), 'error'); }
							}}
							size="sm"
							className="flex-1"
						>
							Supprimer
						</Button>
					</div>
				</div>
			),
			width: 'xs',
			showCloseIcon: false,
		});
	};

	if (isLoading) {
		return (
			<div className="space-y-5">
				<div className="hidden md:block border rounded-md">
					<Table>
						<DataTableHeader />
						<DataTableBodySkeleton rows={8} />
					</Table>
				</div>
				<div className="block md:hidden">
					<DataTableMobileSkeleton count={5} />
				</div>
			</div>
		);
	}

	if (events.length === 0) {
		return (
			<PanelInfo
				title="Aucun événement"
				description="Aucun événement ne correspond aux critères sélectionnés."
				className="py-10 md:py-26 border-none"
				icon={Calendar}
			/>
		);
	}

	return (
		<div className="space-y-5">
			<div className="hidden md:block border rounded-md">
				<Table>
					<DataTableHeader />
					<TableBody>
						{events.map(evt => (
							<DataTableDesktopRow
								key={evt._id}
								event={evt}
								onDelete={handleDeleteClick}
								onViewDetails={onViewDetails}
								canDelete={canDelete(evt)}
							/>
						))}
					</TableBody>
				</Table>
			</div>
			<div className="block md:hidden">
				<div className="space-y-4">
					{events.map(evt => (
						<DataTableMobileCard
							key={evt._id}
							event={evt}
							onDelete={handleDeleteClick}
							onViewDetails={onViewDetails}
							canDelete={canDelete(evt)}
						/>
					))}
				</div>
			</div>

			{data.data?.pagination && <DataTablePagination pageCount={data.data.pagination.pageCount} />}
		</div>
	);
};

export default DataTable;
